import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@/components/common/Modal';
import { formatInr } from '@/components/deposit/depositOptions';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useTranslation } from '@/i18n';
import {
  useGetBankAccountQuery,
  useGetCryptoWalletsQuery,
  useGetUpiAccountQuery,
} from '@/store/api/accountApi';
import { setWithdrawModalOpen } from '@/store/slices/uiSlice';
import { getAvailableBalance } from '@/utils';
import { Icons } from '@/utils/Icons';
import WithdrawAccountList from './WithdrawAccountList';
import WithdrawAmountStep from './WithdrawAmountStep';
import WithdrawConfirmStep from './WithdrawConfirmStep';
import { buildWithdrawAccounts, WithdrawAccount } from './withdrawAccounts';

type Step = 'amount' | 'account' | 'confirm';

/** Mounted only while open, so closing the popup always starts the next withdrawal fresh. */
const WithdrawFlow = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('amount');
  const [amount, setAmount] = useState('');
  const [account, setAccount] = useState<WithdrawAccount | null>(null);

  const banks = useGetBankAccountQuery();
  const upis = useGetUpiAccountQuery();
  const wallets = useGetCryptoWalletsQuery();
  const accounts = useMemo(
    () => buildWithdrawAccounts(banks.data?.banks, upis.data?.upi, wallets.data),
    [banks.data, upis.data, wallets.data]
  );

  const balance = getAvailableBalance(useAppSelector((state) => state.user.walletInfo));
  const amountValue = Number(amount);
  const goBack = step === 'confirm' ? 'account' : step === 'account' ? 'amount' : null;

  const title = (
    <span className="flex items-center gap-3">
      {goBack && (
        <button
          type="button"
          onClick={() => setStep(goBack)}
          aria-label="Back"
          className="-ml-1 flex size-8 items-center justify-center rounded-full text-shell-muted transition hover:bg-shell-raised hover:text-shell-fg"
        >
          <Icons.previous />
        </button>
      )}
      {step === 'amount'
        ? t('common.withdraw')
        : `${t('common.withdraw')} ${formatInr(amountValue)}`}
    </span>
  );

  return (
    <Modal open onClose={onClose} title={title} size="sm">
      {step === 'amount' && (
        <WithdrawAmountStep
          balance={balance}
          amount={amount}
          onAmountChange={setAmount}
          onNext={() => setStep('account')}
        />
      )}
      {step === 'account' && (
        <WithdrawAccountList
          accounts={accounts}
          isLoading={banks.isLoading || upis.isLoading || wallets.isLoading}
          isError={banks.isError || upis.isError || wallets.isError}
          onSelect={(selected) => {
            setAccount(selected);
            setStep('confirm');
          }}
          // Accounts are added on the /withdraw page (its Add Account button).
          onAddAccount={() => {
            onClose();
            navigate('/withdraw');
          }}
        />
      )}
      {step === 'confirm' && account && (
        <WithdrawConfirmStep account={account} amount={amountValue} onSubmitted={onClose} />
      )}
    </Modal>
  );
};

/**
 * Withdraw popup, opened by the header's Withdraw button (`ui.withdrawModalOpen`):
 * coins → a saved account → confirm, with the same requests as the /withdraw page.
 * The /withdraw page is separate and unchanged.
 */
const WithdrawModal = () => {
  const dispatch = useAppDispatch();
  const open = useAppSelector((state) => state.ui.withdrawModalOpen);
  if (!open) return null;
  return <WithdrawFlow onClose={() => dispatch(setWithdrawModalOpen(false))} />;
};

export default WithdrawModal;
